import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { elfLogger } from './elf-logger.service';

/**
 * ELF HTTP logging for axios, ported from one-ap-customer_web's Angular
 * ElfHttpInterceptor. Successful (2xx) responses are logged at INFO; network
 * failures and 5xx responses at ERROR; other non-2xx responses at WARN.
 *
 * Request payloads and response bodies are captured alongside each log. They are
 * masked in two layers before leaving the browser: sensitive object keys
 * (passwords, tokens, card/account numbers, etc.) are replaced with a redaction
 * marker here, and the logger service additionally runs every string attribute
 * through its value-based PII redactor. Bodies are truncated to keep records small.
 *
 * The logger transports via fetch/sendBeacon (not axios), so there is no risk of
 * logging our own log requests.
 */

/** Cap serialized bodies so a large payload/response can't bloat a log record. */
const MAX_BODY_LENGTH = 4096;

/** Cap object traversal depth while masking to avoid pathological structures. */
const MAX_MASK_DEPTH = 8;

/** Object keys whose values are masked regardless of their content. */
const SENSITIVE_KEY_PATTERN =
  /pass(word|code)?|secret|token|authorization|credential|cvv|cvc|\bpin\b|ssn|social|tax_?id|card(_?number|_?num)?|account_?number|acct_?number|routing|otp|mfa|securityanswer|securityquestion/i;

// Track instrumented instances so logging is only attached once per instance.
const instrumented = new WeakSet<object>();

/** Attach ELF request/response logging to an axios instance (idempotent). */
export const installElfAxiosLogging = (instance: AxiosInstance): void => {
  if (!instance || instrumented.has(instance)) {
    return;
  }
  instrumented.add(instance);

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      logHttpSuccess(response);
      return response;
    },
    (error: AxiosError) => {
      logHttpError(error);
      return Promise.reject(error);
    }
  );
};

const logHttpSuccess = (response: AxiosResponse): void => {
  try {
    const config: AxiosRequestConfig = response.config || {};
    const method = (config.method || 'get').toUpperCase();
    const url = sanitizeUrl(resolveUrl(response, config));
    elfLogger.info(`HTTP ${response.status} ${method} ${url}`, {
      'http.request.method': method,
      'url.full': url,
      'http.response.status_code': response.status,
      'http.request.body': serializeBody(config.data),
      'http.response.body': serializeBody(response.data),
      'log.source': 'HttpInterceptor'
    });
  } catch {
    // Logging must never break the request pipeline.
  }
};

const logHttpError = (error: AxiosError): void => {
  try {
    const config: AxiosRequestConfig = error.config || {};
    const method = (config.method || 'get').toUpperCase();
    const response = error.response;
    const status = response ? response.status : 0;
    const url = sanitizeUrl(resolveUrl(response, config));
    const summary = `HTTP ${status} ${method} ${url}`;
    const attributes = {
      'http.request.method': method,
      'url.full': url,
      'http.response.status_code': status,
      'http.request.body': serializeBody(config.data),
      'http.response.body': serializeBody(response ? response.data : undefined),
      'error.message': error.message,
      'error.source': 'HttpInterceptor'
    };

    if (status === 0 || status >= 500) {
      elfLogger.error(summary, attributes);
    } else {
      elfLogger.warn(summary, attributes);
    }
  } catch {
    // Logging must never break the request pipeline.
  }
};

/** Prefer the browser-resolved response URL, otherwise rebuild from the config. */
const resolveUrl = (response: AxiosResponse | undefined, config: AxiosRequestConfig): string => {
  const request = response ? (response.request as { responseURL?: string } | undefined) : undefined;
  if (request && typeof request.responseURL === 'string' && request.responseURL) {
    return request.responseURL;
  }
  const base = config.baseURL || '';
  const path = config.url || '';
  if (base && path) {
    return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }
  return path || base || '';
};

/** Drop query strings so tokens/PII in URLs are never sent to ELF. */
const sanitizeUrl = (url: string | null | undefined): string => {
  if (!url) {
    return '';
  }
  const queryIndex = url.indexOf('?');
  return queryIndex >= 0 ? url.substring(0, queryIndex) : url;
};

/**
 * Turn a request/response body into a masked, size-bounded string suitable for
 * logging. Sensitive keys are redacted; binary/streaming payloads are replaced
 * with a type marker rather than dumped. Returns undefined when there is no body
 * so the attribute is omitted from the log record.
 */
const serializeBody = (body: unknown): string | undefined => {
  if (body === undefined || body === null || body === '') {
    return undefined;
  }
  try {
    const marker = nonSerializableMarker(body);
    if (marker) {
      return marker;
    }
    if (typeof body === 'string') {
      const parsed = tryParseJson(body);
      // Only re-stringify when it parses to a maskable object; otherwise keep the
      // raw string (the logger still runs it through the PII redactor).
      return truncate(parsed !== undefined ? JSON.stringify(maskSensitive(parsed)) : body);
    }
    return truncate(JSON.stringify(maskSensitive(body)));
  } catch {
    return '[unserializable body]';
  }
};

/** Recursively replace values of sensitive keys with a redaction marker. */
const maskSensitive = (value: unknown, depth = 0): unknown => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }
  if (depth >= MAX_MASK_DEPTH) {
    return '[max depth]';
  }
  if (Array.isArray(value)) {
    return value.map((item) => maskSensitive(item, depth + 1));
  }
  const source = value as Record<string, unknown>;
  const masked: Record<string, unknown> = {};
  Object.keys(source).forEach((key) => {
    masked[key] = SENSITIVE_KEY_PATTERN.test(key) ? '[REDACTED]' : maskSensitive(source[key], depth + 1);
  });
  return masked;
};

/** Return a type marker for bodies we should not serialize, otherwise null. */
const nonSerializableMarker = (body: unknown): string | null => {
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return '[FormData]';
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return '[Blob]';
  }
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return '[ArrayBuffer]';
  }
  return null;
};

const tryParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const truncate = (value: string): string => {
  if (value.length <= MAX_BODY_LENGTH) {
    return value;
  }
  return `${value.substring(0, MAX_BODY_LENGTH)}...[truncated ${value.length - MAX_BODY_LENGTH} chars]`;
};
